import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import {_openModal} from '../../shared/reducers/modals';
import {Button, Column, DescriptiveText, Image, LoadingSpinner, Row} from '../../shared';
import RiskBox from '../../shared/components/analyze/RiskBox';
import {SizeMe} from 'react-sizeme';
import {getPreferencesValue, getRiskLabel} from '../../shared/utils/utils';
import Columnizer from '../../shared/components/Columnizer';

const RiskManagement = ({context: {id: userID}, session, openModal, singleColumn = false}) => {
  const breakpoints = {width: 530, height: 250};
  const paddingWidth = 5;
  const userAdvisorsDataUrl = '/user/advisors';
  const investorPreferencesDataUrl = `/preferences/users/${userID}/panels/investorGeneralWeb`;

  const [preferredAdvisor, setPreferredAdvisor] = useState();
  const [riskAdvisors, setRiskAdvisors] = useState();
  const [loading, setLoading] = useState(true);

  const {data: userAdvisors, isValidating: userAdvisorsLoading} = useSWR(userAdvisorsDataUrl);
  const {data: investorPreferences, isValidating: investorPreferencesLoading} = useSWR(investorPreferencesDataUrl);

  const openChangeViewModal = () => openModal({
    type: 'ChangeRiskViewModal',
    advisors: riskAdvisors,
    preferredAdvisor: preferredAdvisor,
    onSuccess: (advisor) => setPreferredAdvisor(advisor),
  });

  useEffect(() => {
    if (userAdvisorsLoading || investorPreferencesLoading) {
      return;
    }

    const preferencesPath = ['investorRiskManagementWeb', 'investorRiskManagementWeb', 'investorRiskManagementAdvisorWeb'];
    const userRiskAdvisors = userAdvisors.filter(v => !!v.risk);
    const riskManagementAdvisorId = getPreferencesValue(investorPreferences, ...preferencesPath);
    const riskManagementAdvisor = userRiskAdvisors.find(v => v.id === riskManagementAdvisorId) || userRiskAdvisors[0];

    setRiskAdvisors(userRiskAdvisors);
    setPreferredAdvisor(riskManagementAdvisor);
    setLoading(false);
  }, [userAdvisorsLoading, investorPreferencesLoading]);

  if (loading) {
    return <LoadingSpinner/>;
  }

  return <SizeMe monitorHeight={true}>
    {({size}) => <Column height={'100px'}
                         width={'fill'}
                         alignSubRow={'stretch'}
                         alignItems={'stretch'}
                         layout={{grow: 1}}>
      <Row alignItems={'left'}>
        {((riskAdvisors.length <= 1) || (size.height >= breakpoints.height || size.width >= breakpoints.width)) && (
          <Row justifySubRow={'left'}>
            <Image src={preferredAdvisor.risk.partnerLogo} maxWidth={'80px'} maxHeight={'35px'}/>
          </Row>
        )}
      </Row>
      <Column width={'fill'}
              alignSubRow={'stretch'}
              alignItems={singleColumn ? 'center' : 'stretch'}
              layout={{grow: 1}}
              style={{overflow: 'scroll'}}>
        <Columnizer itemWidth={((singleColumn ? breakpoints.width : breakpoints.width / 2) - paddingWidth)}
                    width={singleColumn ? breakpoints.width + 'px' : 'fill'}
                    maxHeight={'100%'}
                    justifySubRow={'center'}
                    alignItems={'stretch'}>
          <Column height={'100%'} justifySubColumn={'space between'}>
            <Row>
              {preferredAdvisor.risk.actual && <Column maxWidth={'80px'}>
                <RiskBox title={'Actual'}
                         riskPartner={preferredAdvisor.risk.partner}
                         riskNumber={preferredAdvisor.risk.actual}
                         size={'lg'}
                         type={'portfolio'}/>
              </Column>}
              {preferredAdvisor.risk.target && <Column maxWidth={'80px'}>
                <RiskBox title={'Target'}
                         riskPartner={preferredAdvisor.risk.partner}
                         riskNumber={preferredAdvisor.risk.target}
                         size={'lg'}
                         type={'target'}/>
              </Column>}
            </Row>
            <Row height={'30px'} alignSubRow={'middle'}>
              <DescriptiveText font={'ProximaRegular'} fontSize={'16px'} alignment={'center'}>
                {preferredAdvisor.risk.status}
              </DescriptiveText>
            </Row>
          </Column>
          <Column height={'100%'} justifySubColumn={'bottom'}>
            <Row margin={'0 0 5px 0'}>
              <DescriptiveText font={'ProximaRegular'} fontSize={'12px'} alignment={'center'}>
                {getRiskLabel(preferredAdvisor.risk.partner)} is based on accounts {preferredAdvisor.name} manages.
                {((riskAdvisors.length > 1) && (size.height >= breakpoints.height || size.width >= breakpoints.width)) && (
                  <span style={{'padding-left': '5px'}}>Click “Change View” to view Risk Management data from your other {session.profile.advisorTerm}.</span>
                )}
              </DescriptiveText>
            </Row>
            {(riskAdvisors.length > 1) && (
              <Row height={'30px'}
                   maxWidth={'250px'}
                   alignSubRow={'middle'}
                   justifySubRow={'center'}>
                <Button importance={'secondary'}
                        size={'md'}
                        label={'Change View'}
                        width={'fill'}
                        action={() => openChangeViewModal()}/>
              </Row>
            )}
          </Column>
        </Columnizer>
      </Column>
    </Column>}
  </SizeMe>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = dispatch => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RiskManagement);

RiskManagement.propTypes = {
  /**
   * The parent calling component context
   */
  context: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * Is Session Reducer
   */
  session: PropTypes.shape({
    profile: PropTypes.shape({
      advisors: PropTypes.array,
      advisorTerm: PropTypes.string,
    }),
    risk: PropTypes.shape({
      partnerLogo: PropTypes.string,
      actual: PropTypes.number,
      target: PropTypes.number,
      status: PropTypes.string,
    }),
  }).isRequired,
  /**
   * The open function that dispatches _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
  size: PropTypes.object,
  /**
   * if singleColumn=true, sets Columnizer to display items in one column
   */
  singleColumn: PropTypes.bool,
};
